<template>
  <v-dialog v-model="open" persistent max-width="720px">
    <v-form ref="form" v-model="valid" lazy-validation>
      <v-card>
        <v-card-title>
          <v-icon color="primary" class="me-2">mdi-account-plus</v-icon>
          <span class="text-h5">{{ $t('add_driver') }}</span>
        </v-card-title>

        <v-card-subtitle class="pt-2">
          {{ $t('add_driver_hint') }}
        </v-card-subtitle>

        <v-card-text>
          <v-container>
            <v-row>
              <v-col cols="12" sm="6">
                <v-text-field
                  v-model="name"
                  :rules="nameRules"
                  :label="$t('driver_name') + '*'"
                  required
                ></v-text-field>
              </v-col>
              <v-col cols="12" sm="6">
                <v-text-field
                  v-model="email"
                  :rules="emailRules"
                  :label="$t('Email') + '*'"
                  :hint="$t('driver_email_hint')"
                  persistent-hint
                  required
                ></v-text-field>
              </v-col>
            </v-row>

            <v-row>
              <v-col cols="12" sm="6">
                <v-text-field
                  v-model="telNumber"
                  :label="$t('phone_number')"
                ></v-text-field>
              </v-col>
              <v-col cols="12" sm="6">
                <v-select
                  v-model="busId"
                  :items="busChoices"
                  item-text="text"
                  item-value="value"
                  item-disabled="disabled"
                  :label="$t('assign_bus')"
                  :loading="loadingBuses"
                  clearable
                ></v-select>
              </v-col>
            </v-row>

            <v-divider class="my-4"></v-divider>

            <!-- the password is set here and handed to the driver, rather than
                 mailed as a link that may never arrive -->
            <v-text-field
              v-model="password"
              :rules="passwordRules"
              :label="$t('Password') + '*'"
              :hint="$t('driver_password_hint')"
              persistent-hint
              :append-icon="showPassword ? 'mdi-eye-off' : 'mdi-eye'"
              :type="showPassword ? 'text' : 'password'"
              required
              @click:append="showPassword = !showPassword"
            ></v-text-field>
          </v-container>
        </v-card-text>

        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="blue darken-1" text :disabled="isSaving" @click="close">
            {{ $t('close') }}
          </v-btn>
          <v-btn color="blue darken-1" text :loading="isSaving" @click="submit">
            {{ $t('save') }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-form>
  </v-dialog>
</template>

<script>
/*
 * Creates a driver account from the panel.
 *
 * A school used to have no way of adding a driver at all: the driver had to
 * install the app, sign up, enter the school code and their documents, and then
 * wait to be approved. A driver created here is active immediately and signs in
 * with their own address, which is what a school that has just hired someone
 * actually wants.
 */
export default {
  name: "AddDriverDialog",
  props: {
    value: { type: Boolean, default: false },
  },
  data() {
    return {
      valid: true,
      isSaving: false,
      loadingBuses: false,
      name: "",
      email: "",
      telNumber: "",
      busId: null,
      buses: [],
      password: "",
      showPassword: false,
      nameRules: [(v) => !!v || this.$t("required_field")],
      emailRules: [
        (v) => !!v || this.$t("E-mail is required"),
        (v) => /.+@.+\..+/.test(v) || this.$t("E-mail must be valid"),
      ],
      passwordRules: [
        (v) => !!v || this.$t("password_required"),
        (v) => (v && v.length >= 6) || this.$t("password_min_length"),
      ],
    };
  },
  computed: {
    open: {
      get() {
        return this.value;
      },
      set(val) {
        this.$emit("input", val);
      },
    },
    //every bus of the school is listed. A bus still has one driver, so the
    //ones already taken cannot be picked - but they are shown greyed out with
    //the driver's name, because a bus silently missing from the list reads as
    //a bug rather than as an answer
    busChoices() {
      return this.buses.map((bus) => ({
        value: bus.id,
        disabled: !!bus.driver,
        text: bus.driver
          ? `${bus.license} (${this.$t("capacity")}: ${bus.capacity}) - ${this.$t("driven_by", { name: bus.driver.name })}`
          : `${bus.license} (${this.$t("capacity")}: ${bus.capacity})`,
      }));
    },
  },
  watch: {
    value(isOpen) {
      if (isOpen) {
        this.reset();
        this.loadBuses();
      }
    },
  },
  methods: {
    reset() {
      this.name = "";
      this.email = "";
      this.telNumber = "";
      this.busId = null;
      this.password = "";
      this.showPassword = false;
      if (this.$refs.form) this.$refs.form.resetValidation();
    },
    close() {
      this.open = false;
    },
    //every bus of the school, not only the free ones: a driver may now share
    //a bus with another driver, so hiding the taken ones hid valid choices
    loadBuses() {
      this.loadingBuses = true;
      axios
        .get("/buses/all")
        .then((response) => {
          this.buses = response.data || [];
        })
        .catch(() => {
          //the driver is still created, just without a bus attached
          this.buses = [];
        })
        .then(() => {
          this.loadingBuses = false;
        });
    },
    submit() {
      if (!this.$refs.form.validate()) return;

      const email = this.email.trim();
      const payload = {
        name: this.name.trim(),
        email: email,
        tel_number: this.telNumber ? this.telNumber.trim() : null,
        bus_id: this.busId || null,
        password: this.password,
      };

      this.isSaving = true;
      axios
        .post("/school-drivers/add", payload)
        .then(() => {
          this.$notifySuccess(this.$t("driver_created", { email: email }));
          this.open = false;
          this.$emit("created");
        })
        .catch((error) => {
          this.$notifyError(error, this.$t("error_creating_driver"));
        })
        .then(() => {
          this.isSaving = false;
        });
    },
  },
};
</script>
